import { ObjectToDraw, SpriteAnimData } from './../types';
import P5 from 'p5';
import Entity from './entity';
import SpriteAnimationRenderer from '../spriteAnimationRenderer';
import Player from '../game/player';
import Smoke from '../particles/smoke';
import TextParticle from '../particles/textParticle';
import GameMap from '../game/gameMap';
import GameItem from './gameItem';
import { moveWithCollisions } from '../game/collision';
import PathAi from '../pathFinding/pathAi';
import p5 from 'p5';

class Spider implements Entity {
    NAME = 'spider';

    // Tunable behavior constants - adjust here.
    static ATTACK_RANGE = 200; // player closer than this triggers a chase
    static ATTACK_DISTANCE = 64; // player closer than this gets hit
    static ATTACK_DAMAGE = 50;
    static ATTACK_KNOCKBACK_SPEED = 10;
    static ATTACK_COOLDOWN_MS = 1000;
    static HIT_FOLLOW_DURATION_MS = 2000; // keep chasing this long after being hit, even if the player runs off
    static ARRIVAL_DISTANCE = 5; // "close enough" to a waypoint/origin to call it reached
    static RETURN_TO_ORIGIN = true; // walk back to spawn once the player gets away
    static REPATH_INTERVAL_MS = 500; // recompute the chase/return path at least this often
    static REPATH_TARGET_MOVE = 64; // recompute early once the target has moved this far since the last path

    hash: string;
    x: number;
    y: number;
    vx: number = 0;
    vy: number = 0;
    // Knockback impulse velocity, decays every frame (see knockback()).
    kbVx: number = 0;
    kbVy: number = 0;
    gx: number;
    gy: number;
    speed: number;
    currentDirection: number;
    state: string;
    oldState: string;

    isDead: boolean;
    animIdle: SpriteAnimationRenderer;
    animWalking: SpriteAnimationRenderer;
    animRunning: SpriteAnimationRenderer;
    health: number;
    healthSmooth: number;
    hitbox: {w: number, h: number};
    strength: number;
    damagable: boolean;

    attackCooldownTime: number;
    followPlayerCooldownTime: number;

    pathAi: PathAi;
    // Spawn position, remembered so the spider can walk back to it once it
    // gives up chasing (see RETURN_TO_ORIGIN).
    originX: number;
    originY: number;
    isReturning: boolean = false;
    // Path recompute scheduling (see the walking branch in update()).
    nextRepathTime: number = 0;
    lastPathTargetX: number = 0;
    lastPathTargetY: number = 0;

    constructor(private gameMap: GameMap, sx: number, sy: number) {
        this.hash = `spider_${sx}_${sy}`;

        this.hitbox = {w: 64, h: 48};
        this.damagable = true;
        this.strength = 1;

        this.x = sx;
        this.y = sy;
        this.health = 100;
        this.healthSmooth = this.health;
        this.gx = sx;
        this.gy = sy;
        this.originX = sx;
        this.originY = sy;
        this.speed = 2;
        this.currentDirection = p5js.int(p5js.random(0, 7));
        this.state = 'idle';
        this.attackCooldownTime = p5js.millis();
        this.followPlayerCooldownTime = 0;
        this.isDead = false;
        this.pathAi = new PathAi(this.gameMap, 8);

        this.animIdle = new SpriteAnimationRenderer();
        this.animIdle.load('/assets/entities/spider/idle');

        this.animWalking = new SpriteAnimationRenderer();
        this.animWalking.load('/assets/entities/spider/walking');
    }

    init() {
        console.log('Spider initialized');
    }

    update() {
        let distanceToPlayer = p5js.dist(player.x, player.y, this.x, this.y)
        if(distanceToPlayer < Spider.ATTACK_RANGE){
            this.state = 'walking';
            this.isReturning = false;
        }

        if(distanceToPlayer > Spider.ATTACK_RANGE*2){
            this.state = 'idle';
        }

        if(this.followPlayerCooldownTime > p5js.millis()){
            this.state = 'walking';
            this.isReturning = false;
        }

        if(distanceToPlayer < Spider.ATTACK_DISTANCE){
            this.state = 'idle';
            this.isReturning = false;
            if(this.attackCooldownTime < p5js.millis() && player.health > 0){
                player.hit(Spider.ATTACK_DAMAGE);
                let angleToPlayer = p5js.atan2(player.y-this.y, player.x-this.x);
                let knockbackX = p5js.cos(angleToPlayer) * Spider.ATTACK_KNOCKBACK_SPEED;
                let knockbackY = p5js.sin(angleToPlayer) * Spider.ATTACK_KNOCKBACK_SPEED;
                player.knockback(knockbackX, knockbackY);
                this.attackCooldownTime = p5js.millis() + Spider.ATTACK_COOLDOWN_MS;
            }
        }

        // Gated on the same "out of range" check as the idle transition above
        // (not just state == 'idle') so this doesn't cut short the close-range
        // attack idle, and skipped while followPlayerCooldownTime is still
        // keeping the spider on the player's trail.
        if(distanceToPlayer > Spider.ATTACK_RANGE*2 && this.followPlayerCooldownTime <= p5js.millis()
            && Spider.RETURN_TO_ORIGIN && p5js.dist(this.x, this.y, this.originX, this.originY) > Spider.ARRIVAL_DISTANCE){
            this.state = 'walking';
            this.isReturning = true;
        }

        if(this.state == 'walking'){
            let targetX = this.isReturning ? this.originX : player.x;
            let targetY = this.isReturning ? this.originY : player.y;
            this.gx = targetX;
            this.gy = targetY;

            if(this.isReturning && p5js.dist(this.x, this.y, targetX, targetY) < Spider.ARRIVAL_DISTANCE){
                this.state = 'idle';
                this.isReturning = false;
                this.vx = 0;
                this.vy = 0;
            }else{
                // Recompute the path periodically, and immediately once the
                // target has moved far enough, rather than every frame. The
                // obstacle window follows the spider's current position, so a
                // fresh path also corrects earlier routes that clipped a wall
                // in a chunk that was outside the previous query's window.
                if(p5js.millis() > this.nextRepathTime
                    || p5js.dist(targetX, targetY, this.lastPathTargetX, this.lastPathTargetY) > Spider.REPATH_TARGET_MOVE){
                    this.pathAi.calculatePath(this.x, this.y, targetX, targetY);
                    this.lastPathTargetX = targetX;
                    this.lastPathTargetY = targetY;
                    this.nextRepathTime = p5js.millis() + Spider.REPATH_INTERVAL_MS;
                }

                let nextX = targetX;
                let nextY = targetY;
                if(this.pathAi.path.length > 1){
                    for(let waypoint of this.pathAi.path){
                        if(!waypoint.explored){
                            if(p5js.dist(this.x, this.y, waypoint.x, waypoint.y) < Spider.ARRIVAL_DISTANCE){
                                waypoint.explored = true;
                            }else{
                                nextX = waypoint.x;
                                nextY = waypoint.y;
                                break;
                            }
                        }
                    }
                }

                let angleToNextWaypoint = p5js.atan2(nextY - this.y, nextX - this.x);
                this.vx = p5js.int(p5js.cos(angleToNextWaypoint) * this.speed);
                this.vy = p5js.int(p5js.sin(angleToNextWaypoint) * this.speed);
                this.currentDirection = (p5js.int(p5js.map(p5js.atan2(this.vy, this.vx), -p5js.PI, p5js.PI, 0, 7))+3) % 8;
            }
        }else{
            this.vx = 0;
            this.vy = 0;
        }

        let moveResult = moveWithCollisions(this.gameMap, this.x, this.y, this.vx + this.kbVx, this.vy + this.kbVy, 5);
        this.x = moveResult.x;
        this.y = moveResult.y;

        this.kbVx *= 0.9;
        this.kbVy *= 0.9;
        if(p5js.max(p5js.abs(this.kbVx), p5js.abs(this.kbVy)) < 1){
            this.kbVx = 0;
            this.kbVy = 0;
        }

        this.oldState = this.state;
    }

    render(objectsToDraw: ObjectToDraw[]) {
        let posOnScreen = {x: this.x-player.x, y: this.y-player.y};

        if(posOnScreen.x < -buffer.width/2-64 || posOnScreen.x > buffer.width/2+64 || posOnScreen.y < -buffer.height/2-64 || posOnScreen.y > buffer.height/2+64){
            return;
        }

        if(showDebug){
            buffer.stroke(255);
            buffer.strokeWeight(1);
            buffer.line(this.x-player.x, this.y-player.y, this.gx-player.x, this.gy-player.y);

            buffer.noStroke();
            buffer.fill(255, 0, 0);
            buffer.ellipse(this.gx-player.x, this.gy-player.y, 10, 10);

            buffer.fill(255);
            buffer.textAlign(p5js.CENTER, p5js.TOP);
            let cooldownSecondsLeft = p5js.int((this.followPlayerCooldownTime-p5js.millis())/1000);
            if(cooldownSecondsLeft < 0){
                cooldownSecondsLeft = 0;
            }
            buffer.text(this.state + ": " + cooldownSecondsLeft + "s", this.x-player.x, this.y-player.y+10);

            buffer.stroke(0, 0, 255);
            buffer.strokeWeight(1);
            buffer.noFill();
            buffer.rect(this.x-player.x-this.hitbox.w/2, this.y-player.y-this.hitbox.h, this.hitbox.w, this.hitbox.h);

            buffer.noFill();
            buffer.stroke(255, 0, 0);
            buffer.ellipse(this.x-player.x, this.y-player.y, Spider.ATTACK_RANGE*2, Spider.ATTACK_RANGE*2);
            buffer.ellipse(this.x-player.x, this.y-player.y, Spider.ATTACK_RANGE*4, Spider.ATTACK_RANGE*4);

            if(this.state == 'walking'){
                this.pathAi.renderDebug();
            }
        }

        buffer.noStroke();
        buffer.fill(0, 100);
        buffer.ellipse(this.x-player.x, this.y-player.y, 60, 30);

        if(this.state == 'walking'){
            this.animWalking.render(objectsToDraw, this.x-player.smoothX, this.y-player.smoothY, this.currentDirection);
        }else if (this.state == 'idle'){
            this.animIdle.render(objectsToDraw, this.x-player.smoothX, this.y-player.smoothY, this.currentDirection);
        }
    }

    renderUI(): void {
        let offsetY = -64;
        let barWidth = 20;
        let healthWidth = p5js.map(this.health, 0, 100, -barWidth, barWidth);
        let healthSmoothWidth = p5js.map(this.healthSmooth, 0, 100, -barWidth, barWidth);
        buffer.stroke(0);
        buffer.strokeWeight(6);
        buffer.line(this.x-player.x-barWidth, this.y-player.y+offsetY, this.x-player.x+barWidth, this.y-player.y+offsetY);
        buffer.stroke(255);
        buffer.strokeWeight(4);
        buffer.line(this.x-player.x-barWidth, this.y-player.y+offsetY, this.x-player.x+healthSmoothWidth, this.y-player.y+offsetY);
        buffer.stroke(255, 0, 0);
        buffer.line(this.x-player.x-barWidth, this.y-player.y+offsetY, this.x-player.x+healthWidth, this.y-player.y+offsetY);
        this.healthSmooth = p5js.lerp(this.healthSmooth, this.health, 0.1);
        buffer.noStroke();
        buffer.fill(255);
        buffer.textAlign(p5js.CENTER, p5js.BOTTOM);
        buffer.textSize(10);
        buffer.text('Spider', this.x-player.x, this.y-player.y+offsetY-5);
    }

    isToBeRemoved() {
        return this.isDead;
    }

    kill() {
        this.isDead = true;
    }

    hit(damage: number){
        if(this.health <= 0){
            return;
        }
        let damageDealt = p5js.floor(damage / this.strength);
        this.health -= damageDealt
        particles.push(new TextParticle(this.x + p5js.random(-30, 30), this.y + p5js.random(-30, 30), `-${damageDealt}hp`));
        if(this.health <= 0){
            this.health = 0;
            particles.push(new Smoke(this.x, this.y, 10));

            for(let i = 0; i < 5; i++){
                entities.push(new GameItem("coin", this.x, this.y, p5js.random(1, 7)));
            }

            this.kill();
        }else{
            particles.push(new Smoke(this.x, this.y, 3));
        }

        this.followPlayerCooldownTime = p5js.millis() + Spider.HIT_FOLLOW_DURATION_MS;
    }

    knockback(x: number, y: number){
        this.kbVx = x;
        this.kbVy = y;
    }

    getHash() {
        return this.hash;
    }

    isInHitbox(px: number, py: number): boolean {
        return px > this.x - this.hitbox.w/2 && px < this.x + this.hitbox.w/2 && py > this.y - this.hitbox.h && py < this.y;
    }
}

export default Spider;
